import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Home, 
  Package, 
  Cloud, 
  Settings, 
  Menu, 
  X,
  BookOpen,
  Sparkles,
  Leaf,
  Dna,
  Calendar,
  Heart
} from 'lucide-react'
import { cn } from '../lib/utils'
import { NotificationBell } from '../contexts/NotificationContext'
import { ThemeToggle } from './ThemeToggle'

interface LayoutProps {
  children: React.ReactNode
}

const navigation = [
  { name: 'Dashboard', href: '/', icon: Home, color: 'from-success to-success/80' },
  { name: 'Stock Tracker', href: '/stock', icon: Package, color: 'from-info to-info/80' },
  { name: 'Mutation Tracker', href: '/weather', icon: Dna, color: 'from-purple to-purple/80' },
  { name: 'Event Tracker', href: '/events', icon: Calendar, color: 'from-orange to-orange/80' },
  { name: 'Pet Wiki', href: '/pets', icon: Heart, color: 'from-pink to-pink/80' },
  { name: 'Game Guide', href: '/guide', icon: BookOpen, color: 'from-orange to-orange/80' },
  { name: 'Settings', href: '/settings', icon: Settings, color: 'from-pink to-pink/80' },
]

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Animated background particles */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-20 left-20 w-2 h-2 bg-primary/20 rounded-full float"></div>
        <div className="absolute top-40 right-32 w-1 h-1 bg-info/30 rounded-full float" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-32 left-32 w-1.5 h-1.5 bg-purple/25 rounded-full float" style={{ animationDelay: '4s' }}></div>
        <div className="absolute bottom-20 right-20 w-1 h-1 bg-orange/20 rounded-full float" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Mobile sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 lg:hidden"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
            <motion.div
              initial={{ x: -400 }}
              animate={{ x: 0 }}
              exit={{ x: -400 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 bottom-0 w-80 glass-card shadow-elegant-lg"
            >
              <div className="flex items-center justify-between p-6 border-b border-border/30">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-xl bg-gradient-to-r from-primary to-primary/80">
                    <Sparkles className="h-5 w-5 text-primary-foreground" />
                  </div>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 hover:bg-accent rounded-xl transition-modern hover:scale-105"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="p-4 space-y-2">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href
                  return (
                    <motion.div
                      key={item.name}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Link
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={cn(
                          "nav-item group relative overflow-hidden",
                          isActive ? "nav-item-active" : "nav-item-inactive"
                        )}
                      >
                        <div className={cn(
                          "absolute inset-0 rounded-xl transition-all duration-300",
                          isActive ? `bg-gradient-to-r ${item.color}` : "bg-accent/0 group-hover:bg-accent/20"
                        )} />
                        <div className="relative flex items-center space-x-3">
                          <div className={cn(
                            "p-2 rounded-lg transition-all duration-300",
                            isActive ? "bg-white/20" : "bg-muted/50 group-hover:bg-muted/70"
                          )}>
                            <item.icon className="h-5 w-5" />
                          </div>
                          <span className="font-medium">{item.name}</span>
                        </div>
                      </Link>
                    </motion.div>
                  )
                })}
              </nav>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-80 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto glass-card px-6 pb-4 shadow-elegant">
          <div className="flex h-20 shrink-0 items-center">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-xl bg-gradient-to-r from-primary to-primary/80 shadow-lg">
                <Sparkles className="h-5 w-5 text-primary-foreground" />
              </div>
            </div>
          </div>
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-2">
                  {navigation.map((item) => {
                    const isActive = location.pathname === item.href
                    return (
                      <li key={item.name}>
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Link
                            to={item.href}
                            className={cn(
                              "nav-item group relative overflow-hidden",
                              isActive ? "nav-item-active" : "nav-item-inactive"
                            )}
                          >
                            <div className={cn(
                              "absolute inset-0 rounded-xl transition-all duration-300",
                              isActive ? `bg-gradient-to-r ${item.color}` : "bg-accent/0 group-hover:bg-accent/20"
                            )} />
                            <div className="relative flex items-center space-x-3">
                              <div className={cn(
                                "p-2 rounded-lg transition-all duration-300",
                                isActive ? "bg-white/20" : "bg-muted/50 group-hover:bg-muted/70"
                              )}>
                                <item.icon className="h-6 w-6 shrink-0" />
                              </div>
                              <span className="font-medium text-foreground">{item.name}</span>
                            </div>
                          </Link>
                        </motion.div>
                      </li>
                    )
                  })}
                </ul>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-80">
        {/* Enhanced Top bar */}
        <header className="sticky top-0 z-40 w-full border-b bg-background/80 backdrop-blur-xl">
          <div className="container mx-auto px-4 h-16 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-2">
                <div className="p-2 rounded-xl bg-gradient-to-r from-primary/20 to-primary/10 border border-primary/30">
                  <Leaf className="h-6 w-6 text-primary" />
                </div>
                <span className="text-xl font-bold text-foreground">Grow a Garden</span>
              </Link>
            </div>
            
            <div className="flex items-center space-x-4">
              <ThemeToggle />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-2 hover:bg-accent rounded-xl transition-modern"
              >
                <NotificationBell />
              </motion.button>
              
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 hover:bg-accent rounded-xl transition-modern hover:scale-105 md:hidden"
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="py-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
} 